<template>
  <v-col
    style="flex: 0 0 310px; min-width: 0"
  >
    <v-hover v-slot="{ hover }">
      <v-card
        color="grey lighten-4"
        rounded="lg"
        :elevation="hover ? 10 : 5"
        class="fill-height"
        @click="$emit('click')"
      >
        <div class="d-flex flex-column fill-height">
          <div class="pa-4 grey--text text--darken-2 col col-auto">
            <v-row
              no-gutters
              align="center"
              style="min-height: 28px"
            >
              <v-col>
                <div class="font-weight-medium">
                  {{ group.title }}
                </div>
              </v-col>
              <v-col
                cols="auto"
                :class="{ 'd-none': !hover }"
              >
                <v-btn
                  text
                  small
                  color="grey darken-2"
                  class="ma-0"
                  @click="editDialog = true"
                >
                  <v-icon x-small>
                    far fa-pen
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
          <v-divider />
          <div class="pa-4 grey--text text-body-1 text--darken-2 col">
            <div
              v-if="group.description"
              class="caption mb-3"
            >
              {{ group.description }}
            </div>
            <ul>
              <li
                v-for="(item, index) in group.exercises"
                :key="group.id + '_' + item.exercise_id + '_' + index"
                class="mb-1"
              >
                <div v-if="item.type === 'superset'">
                  {{ $t("training_builder.superset") }} x{{ item.params.NMSR }}
                  <ul
                    v-if="item.exercises.length > 0"
                    class="pl-3 pt-1"
                  >
                    <li
                      v-for="supersetExercise in (item.exercises || [])"
                      :key="'superset_' + group.id + '_' + index + '_' + supersetExercise.exercise_id"
                    >
                      <span v-if="supersetExercise.type === 'pause'">
                        Pause {{ supersetExercise.params.DURATION }} s
                      </span>
                      <span v-else-if="supersetExercise.type === 'exercise'">
                        {{ supersetExercise.name }} x{{ supersetExercise.params.SETS.length }}
                      </span>
                    </li>
                  </ul>
                </div>
                <div v-else-if="item.type === 'pause'">
                  Pause {{ item.params.DURATION }} s
                </div>
                <div v-else-if="item.type === 'exercise'">
                  {{ item.name }} x{{ item.params.SETS.length }}
                </div>
              </li>
            </ul>
            <div
              v-if="group.tags.length > 0"
              class="mt-4"
            >
              <v-chip
                v-for="tag in group.tags"
                :key="group.id + '_' + tag.id"
                small
                class="mr-1 mb-1"
              >
                <span class="text-truncate">
                  {{ $t(`training_exercise_tags.labels.${toCamelCase(tag.id)}`) }}
                </span>
              </v-chip>
            </div>
          </div>
          <v-divider v-if="allowAddToProtocol" />
          <div
            v-if="allowAddToProtocol"
            class="pa-3 col col-auto"
          >
            <v-row
              no-gutters
              align="center"
            >
              <v-col>
                <v-btn
                  text
                  block
                  class="text-none"
                  @click="$emit('add')"
                >
                  <v-icon
                    small
                    left
                  >
                    far fa-plus
                  </v-icon>
                  {{ $t('training_builder.button.add_to_protocol') }}
                </v-btn>
              </v-col>
              <v-col cols="auto" />
            </v-row>
          </div>
        </div>
      </v-card>
    </v-hover>

    <card-dialog
      v-model="editDialog"
      :title="$t('card_dialog.edit_saved_section.title')"
      max-width="400px"
    >
      <v-card-text class="pa-4">
        <validation-observer ref="validationObserver">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="title"
              outlined
              :label="$t('training_builder.text_input.section_title.title')"
              :error-messages="errors"
            />

            <v-combobox
              v-model="selectedTags"
              outlined
              multiple
              hide-details
              :label="$t('dropdown.section_tags.title')"
              dense
              chips
              small-chips
              deletable-chips
              :items="tags.data"
              :loading="tags.loading"
              return-object
              item-value="id"
              item-text="label"
              :delimiters="[',']"
            >
              <template v-slot:selection="data">
                <v-tooltip
                  bottom
                  :disabled="data.item.label && data.item.label.length < 50"
                >
                  <template v-slot:activator="{ on }">
                    <v-chip
                      small
                      v-on="on"
                    >
                      <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                        {{ data.item.label ? data.item.label : data.item }}
                      </span>
                      <v-icon
                        x-small
                        right
                        @click="selectedTags.splice(selectedTags.indexOf(data.item), 1)"
                      >
                        fal fa-times
                      </v-icon>
                    </v-chip>
                  </template>
                  {{ data.item.label ? data.item.label : data.item }}
                </v-tooltip>
              </template>
            </v-combobox>
          </validation-provider>
        </validation-observer>
      </v-card-text>

      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <div class="flex-grow-1">
            <v-btn
              color="primary"
              block
              large
              :loading="submitting"
              @click="submit()"
            >
              {{ $t('training_builder.button.save') }}
            </v-btn>
            <v-btn
              color="error"
              block
              text
              class="mt-2"
              :loading="deleting"
              @click="deleteConfirmationDialogVisible = true"
            >
              {{ $t("button.delete") }}
            </v-btn>
          </div>
        </v-card-actions>
      </template>
    </card-dialog>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteGroup()"
    />
  </v-col>
</template>
<script>
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import CardDialog from '@/components/helpers/CardDialog.vue'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import { TagApiService } from '@/data/services/TagApiService'
import { toCamelCase } from '@/utils/helpers'

export default {
  name: 'UserExerciseGroupCard',
  components: {
    DeleteConfirmationDialog,
    CardDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    group: {
      type: Object,
      required: true
    },
    allowAddToProtocol: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      editDialog: false,
      title: null,
      selectedTags: [],
      items: [],
      submitting: false,
      tags: {
        loading: false,
        data: []
      }
    }
  },
  created() {
    this.title = this.group.title
    this.selectedTags = this.group.tags

    this.fetchTags()
  },
  methods: {
    toCamelCase,
    async submit() {
      if (!(await this.$refs.validationObserver.validate())) return

      await this.save()
    },
    async save() {
      this.submitting = true

      try {
        await TrainingExerciseApiService.saveUserGroup({
          id: this.group.id,
          params: {
            title: this.title
          },
          tags: this.selectedTags.map(t => {
            if (Array.isArray(t)) return t.id

            return t
          })
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.section_updated.success'),
          color: 'success'
        })

        this.$emit('saved')

        this.editDialog = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.saving_selection.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    },
    async deleteGroup() {
      try {
        this.deleting = true

        await TrainingExerciseApiService.deleteUserGroup(this.group.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.section_deleted.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false
        this.$emit('saved')

        this.editDialog = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.deleting_group.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async fetchTags() {
      this.tags.loading = true

      try {
        const result = await TagApiService.getAll()
        this.tags.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_tags.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.tags.loading = false
      }
    }
  }
}
</script>
